import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, IsoDateTime, UserRef } from '@/shared/api/types';
import type { CommentItem, CommentsAdapter } from '@/shared/components/comments';

/*
 * Universal lists API (backend module `lists`, route root /api/lists — docs/modules/lists.md).
 * Query keys start with the module id `lists`.
 */

export type ListKind = 'list' | 'feedProcess';
export type ListRole = 'none' | 'read' | 'add' | 'addRead' | 'edit' | 'admin';
export type PrincipalType = 'everyone' | 'department' | 'group' | 'user';
export type HistoryKind = 'created' | 'updated' | 'deleted' | 'restored' | 'log';

export type FieldType =
  | 'string'
  | 'text'
  | 'int'
  | 'number'
  | 'money'
  | 'bool'
  | 'date'
  | 'datetime'
  | 'user'
  | 'enum'
  | 'file'
  | 'url'
  | 'email'
  | 'phone'
  | 'listItem'
  | 'crm';

export const FIELD_TYPES: readonly FieldType[] = [
  'string',
  'text',
  'int',
  'number',
  'money',
  'bool',
  'date',
  'datetime',
  'user',
  'enum',
  'file',
  'url',
  'email',
  'phone',
  'listItem',
  'crm',
];

/** Roles that can be given, weakest first. */
export const ROLES: readonly Exclude<ListRole, 'none'>[] = ['read', 'add', 'addRead', 'edit', 'admin'];

export const CRM_TYPES = ['lead', 'deal', 'contact', 'company'] as const;
export type CrmType = (typeof CRM_TYPES)[number];

export interface ListRights {
  readAll: boolean;
  add: boolean;
  editAll: boolean;
  admin: boolean;
}

export interface ListSummary {
  id: string;
  code: string | null;
  name: string;
  description: string;
  icon: string;
  kind: ListKind;
  itemCount: number;
  myRole: ListRole;
  rights: ListRights;
  isArchived: boolean;
  updatedAt: IsoDateTime;
}

export interface Catalog {
  lists: ListSummary[];
  canCreate: boolean;
}

export interface FieldOption {
  id: string;
  label: string;
  color: string | null;
}

/** A stored value of a field (docs/modules/bizproc.md §3.3); arrays for multiple fields. */
/** A JSON value as stored. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type FieldValue = JsonValue;

export interface FieldSettings {
  options: FieldOption[];
  defaultValue: FieldValue;
  minLength: number | null;
  maxLength: number | null;
  min: number | null;
  max: number | null;
  pattern: string | null;
  currency: string | null;
  listId: string | null;
  crmTypes: string[];
  hint: string | null;
}

export interface Field {
  id: string;
  code: string;
  label: string;
  type: FieldType;
  isRequired: boolean;
  isMultiple: boolean;
  settings: FieldSettings;
  showInTable: boolean;
  showInForm: boolean;
  showInFilter: boolean;
  isUnique: boolean;
  processOnly: boolean;
  sortOrder: number;
}

export interface ListDetail {
  id: string;
  code: string | null;
  name: string;
  description: string;
  icon: string;
  kind: ListKind;
  titleTemplate: string | null;
  numbering: boolean;
  lockAfterSubmit: boolean;
  isArchived: boolean;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  myRole: ListRole;
  rights: ListRights;
  fields: Field[];
  documentType: string;
}

export interface ItemRef {
  id: string;
  listId: string;
  title: string | null;
  number: number | null;
  hidden: boolean;
}

export interface CrmRef {
  type: string;
  id: string;
  title: string | null;
  url: string | null;
  hidden: boolean;
}

/** What values point to, resolved for the reader (CRM keys: `crm.company:<id>`). */
export interface Refs {
  users: Record<string, UserRef>;
  files: Record<string, FileRef>;
  items: Record<string, ItemRef>;
  crm: Record<string, CrmRef>;
}

export const EMPTY_REFS: Refs = { users: {}, files: {}, items: {}, crm: {} };

export type Values = Record<string, FieldValue>;

export interface ItemRow {
  id: string;
  number: number;
  title: string;
  values: Values;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  canEdit: boolean;
  deletedAt: IsoDateTime | null;
}

export interface ItemPage {
  items: ItemRow[];
  page: number;
  pageSize: number;
  total: number;
  refs: Refs;
}

export interface ItemDetail {
  id: string;
  listId: string;
  number: number;
  title: string;
  values: Values;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  updatedBy: UserRef | null;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
  permissions: { edit: boolean; delete: boolean; comment: boolean; restore: boolean };
  locked: boolean;
  refs: Refs;
  list: ListDetail;
}

export interface Actor {
  kind: string;
  user: UserRef | null;
  name: string | null;
}

export interface HistoryEntry {
  id: string;
  at: IsoDateTime;
  kind: HistoryKind;
  actor: Actor;
  fieldCode: string | null;
  fieldLabel: string | null;
  fieldType: FieldType | null;
  oldValue: FieldValue;
  newValue: FieldValue;
  text: string | null;
}

export interface HistoryPage {
  items: HistoryEntry[];
  page: number;
  pageSize: number;
  total: number;
  refs: Refs;
}

export interface Subject {
  type: PrincipalType;
  id: string | null;
  user: UserRef | null;
  group: { id: string; name: string; avatarUrl: string | null; isProject: boolean } | null;
  hidden?: boolean;
}

export interface Permission {
  subject: Subject;
  role: ListRole;
}

export interface PermissionInput {
  type: PrincipalType;
  id: string | null;
  role: ListRole;
}

export interface ViewDefinition {
  columns: string[];
  sort: string | null;
  filters: Record<string, string>;
  search: string | null;
}

export interface SavedView {
  id: string;
  name: string;
  shared: boolean;
  canEdit: boolean;
  definition: ViewDefinition;
}

export interface CrmSearchResult {
  type: string;
  id: string;
  title: string;
}

export interface ListInput {
  name: string;
  description?: string | null;
  icon?: string | null;
  kind?: ListKind;
  code?: string | null;
  numbering?: boolean;
  lockAfterSubmit?: boolean | null;
}

export interface ListUpdate {
  name: string;
  description: string | null;
  icon: string | null;
  code: string | null;
  titleTemplate: string | null;
  numbering: boolean;
  lockAfterSubmit: boolean;
  archived: boolean;
}

export interface FieldSettingsInput {
  options?: { id: string | null; label: string; color?: string | null }[];
  defaultValue?: FieldValue;
  minLength?: number | null;
  maxLength?: number | null;
  min?: number | null;
  max?: number | null;
  pattern?: string | null;
  currency?: string | null;
  listId?: string | null;
  crmTypes?: string[] | null;
  hint?: string | null;
}

export interface FieldInput {
  label: string;
  type?: FieldType;
  isRequired: boolean;
  isMultiple?: boolean;
  settings: FieldSettingsInput;
  showInTable: boolean;
  showInForm: boolean;
  showInFilter: boolean;
  isUnique: boolean;
  processOnly: boolean;
}

/** The items query: search, sort, paging and `f.<code>[.from|.to]` filters. */
export interface ItemQuery {
  search?: string;
  sort?: string | null;
  page?: number;
  pageSize?: number;
  deleted?: boolean;
  filters?: Record<string, string>;
}

/** A process the caller may start from the feed's composer (`GET /api/lists/feed-processes`). */
export interface FeedProcess {
  id: string;
  code: string | null;
  name: string;
  description: string;
  icon: string;
  documentType: string;
}

/** A ready-made list of an employee process in the gallery (`GET /api/lists/templates`). */
export interface ListTemplate {
  code: string;
  name: string;
  description: string;
  icon: string;
  /** Templates whose lists must be installed first. */
  requires: string[];
  fieldCount: number;
  /** The installed list, when the caller sees it. */
  installed: { listId: string; name: string; documentType: string } | null;
}

/** The outcome of installing a list template: the list and the document type the process runs on. */
export interface InstalledTemplate {
  listId: string;
  documentType: string;
  created: boolean;
  list: ListDetail;
}

/** Who besides the author and their manager sees the feed entry of a new request of a feed process. */
export interface FeedProcessSettings {
  observers: UserRef[];
}

/* ------------------------------------------------------------------ query keys */

export const listsKeys = {
  all: ['lists'] as const,
  feedProcesses: () => ['lists', 'feed-processes'] as const,
  templates: () => ['lists', 'templates'] as const,
  feedSettings: (listId: string) => ['lists', 'feed-settings', listId] as const,
  catalog: (archived: boolean) => ['lists', 'catalog', archived] as const,
  catalogs: () => ['lists', 'catalog'] as const,
  list: (id: string) => ['lists', 'list', id] as const,
  items: (listId: string) => ['lists', 'items', listId] as const,
  itemsPage: (listId: string, query: ItemQuery) => ['lists', 'items', listId, query] as const,
  item: (id: string) => ['lists', 'item', id] as const,
  history: (id: string) => ['lists', 'history', id] as const,
  comments: (id: string) => ['lists', 'comments', id] as const,
  views: (listId: string) => ['lists', 'views', listId] as const,
  permissions: (listId: string) => ['lists', 'permissions', listId] as const,
  settings: () => ['lists', 'settings'] as const,
};

/** SPA path of a list and the deep link of an item (its list with the card open). */
export const listPath = (id: string) => `/lists/${id}`;
export const itemLink = (listId: string, itemId: string) => `/lists/${listId}?panel=list-item:${itemId}`;

/** A query value, or undefined when empty (the URL leaves it out). */
const nonEmpty = (value: string | null | undefined) => (value === null || value === '' ? undefined : value);

const toQuery = (query: ItemQuery) => ({
  search: nonEmpty(query.search),
  sort: nonEmpty(query.sort),
  page: query.page,
  pageSize: query.pageSize,
  deleted: query.deleted || undefined,
  ...query.filters,
});

/* ------------------------------------------------------------------ calls */

export const listsApi = {
  catalog: (archived: boolean, signal?: AbortSignal) =>
    api.get<Catalog>('/lists', { query: { archived: archived || undefined }, signal }),
  list: (id: string, signal?: AbortSignal) => api.get<ListDetail>(`/lists/${id}`, { signal }),
  create: (input: ListInput) => api.post<ListDetail>('/lists', input),
  update: (id: string, input: ListUpdate) => api.put<ListDetail>(`/lists/${id}`, input),
  remove: (id: string) => api.delete(`/lists/${id}`),

  addField: (listId: string, input: FieldInput) => api.post<Field>(`/lists/${listId}/fields`, input),
  updateField: (fieldId: string, input: FieldInput) => api.put<Field>(`/lists/fields/${fieldId}`, input),
  removeField: (fieldId: string) => api.delete(`/lists/fields/${fieldId}`),
  orderFields: (listId: string, ids: string[]) => api.put<Field[]>(`/lists/${listId}/fields/order`, { ids }),

  permissions: (listId: string, signal?: AbortSignal) =>
    api.get<Permission[]>(`/lists/${listId}/permissions`, { signal }),
  setPermissions: (listId: string, entries: PermissionInput[]) =>
    api.put<Permission[]>(`/lists/${listId}/permissions`, { entries }),

  settings: (signal?: AbortSignal) => api.get<{ creators: Subject[] }>('/lists/settings', { signal }),
  setSettings: (creators: { type: PrincipalType; id: string | null }[]) =>
    api.put<{ creators: Subject[] }>('/lists/settings', { creators }),

  items: (listId: string, query: ItemQuery, signal?: AbortSignal) =>
    api.get<ItemPage>(`/lists/${listId}/items`, { query: toQuery(query), signal }),
  item: (id: string, signal?: AbortSignal) => api.get<ItemDetail>(`/lists/items/${id}`, { signal }),
  createItem: (listId: string, input: { title?: string | null; values: Values }) =>
    api.post<ItemDetail>(`/lists/${listId}/items`, input),
  updateItem: (id: string, input: { title?: string | null; values?: Values }) =>
    api.patch<ItemDetail>(`/lists/items/${id}`, input),
  removeItem: (id: string) => api.delete(`/lists/items/${id}`),
  restoreItem: (id: string) => api.post<ItemDetail>(`/lists/items/${id}/restore`),
  history: (id: string, page: number, signal?: AbortSignal) =>
    api.get<HistoryPage>(`/lists/items/${id}/history`, { query: { page, pageSize: 50 }, signal }),

  feedProcesses: (signal?: AbortSignal) => api.get<FeedProcess[]>('/lists/feed-processes', { signal }),
  templates: (signal?: AbortSignal) => api.get<ListTemplate[]>('/lists/templates', { signal }),
  installTemplate: (code: string, readers: string[]) =>
    api.post<InstalledTemplate>(`/lists/templates/${code}/install`, { readers }),
  feedSettings: (listId: string, signal?: AbortSignal) =>
    api.get<FeedProcessSettings>(`/lists/${listId}/feed-process`, { signal }),
  setFeedSettings: (listId: string, observers: string[]) =>
    api.put<FeedProcessSettings>(`/lists/${listId}/feed-process`, { observers }),

  views: (listId: string, signal?: AbortSignal) => api.get<SavedView[]>(`/lists/${listId}/views`, { signal }),
  createView: (listId: string, input: { name: string; shared: boolean; definition: ViewDefinition }) =>
    api.post<SavedView>(`/lists/${listId}/views`, input),
  updateView: (
    listId: string,
    id: string,
    input: { name: string; shared: boolean; definition: ViewDefinition },
  ) => api.put<SavedView>(`/lists/${listId}/views/${id}`, input),
  removeView: (listId: string, id: string) => api.delete(`/lists/${listId}/views/${id}`),

  crmSearch: (types: string[], search: string, signal?: AbortSignal) =>
    api.get<CrmSearchResult[]>('/lists/crm-search', {
      query: { types: types.join(','), search: search || undefined },
      signal,
    }),

  /** The CSV of the current query; `truncated` when the list had more rows than the export holds. */
  exportCsv: async (listId: string, query: ItemQuery): Promise<{ blob: Blob; truncated: boolean }> => {
    let truncated = false;
    const body = await api.get<Blob | string>(`/lists/${listId}/export`, {
      query: { format: 'csv', ...toQuery({ ...query, page: undefined, pageSize: undefined }) },
      headers: { Accept: '*/*' },
      onResponse: (response) => {
        truncated = response.headers.get('X-Orbita-Truncated') === 'true';
      },
    });
    // Decoding the text dropped the byte order mark Excel needs to read Cyrillic: put it back.
    const blob = typeof body === 'string' ? new Blob(['﻿', body], { type: 'text/csv;charset=utf-8' }) : body;
    return { blob, truncated };
  },
};

/** Comments of an item for the shared `CommentsThread`. */
export const commentsAdapter = (itemId: string): CommentsAdapter => ({
  queryKey: listsKeys.comments(itemId),
  list: ({ cursor, signal }) =>
    api.get<CursorPage<CommentItem>>(`/lists/items/${itemId}/comments`, {
      query: { cursor: cursor ?? undefined },
      signal,
    }),
  create: (input) =>
    api.post<CommentItem>(`/lists/items/${itemId}/comments`, {
      bodyHtml: input.bodyHtml,
      attachmentIds: input.attachmentIds,
    }),
  update: (id, input) =>
    api.put<CommentItem>(`/lists/comments/${id}`, {
      bodyHtml: input.bodyHtml,
      attachmentIds: input.attachmentIds,
    }),
  remove: (id) => api.delete(`/lists/comments/${id}`),
});

/* ------------------------------------------------------------------ hooks */

export const useCatalog = (archived = false) =>
  useQuery({
    queryKey: listsKeys.catalog(archived),
    queryFn: ({ signal }) => listsApi.catalog(archived, signal),
  });

/** The processes the user may start from the feed. */
export const useFeedProcesses = (enabled = true) =>
  useQuery({
    queryKey: listsKeys.feedProcesses(),
    queryFn: ({ signal }) => listsApi.feedProcesses(signal),
    enabled,
  });

/** The gallery of ready-made lists of the employee processes. */
export const useListTemplates = (enabled = true) =>
  useQuery({
    queryKey: listsKeys.templates(),
    queryFn: ({ signal }) => listsApi.templates(signal),
    enabled,
  });

export const useList = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: listsKeys.list(id ?? ''),
    queryFn: ({ signal }) => listsApi.list(id ?? '', signal),
    enabled: !!id && enabled,
  });

export const useItems = (listId: string, query: ItemQuery, enabled = true) =>
  useQuery({
    queryKey: listsKeys.itemsPage(listId, query),
    queryFn: ({ signal }) => listsApi.items(listId, query, signal),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useItem = (id: string) =>
  useQuery({ queryKey: listsKeys.item(id), queryFn: ({ signal }) => listsApi.item(id, signal) });

export const useViews = (listId: string) =>
  useQuery({ queryKey: listsKeys.views(listId), queryFn: ({ signal }) => listsApi.views(listId, signal) });
